import React from 'react';
import { useParams } from 'react-router';
import { useNavigateWithBreadcrumbs } from '../../hooks';
import { ROUTES } from '../../constants';
import i18n from '../../utilities/i18n';
import StudyHeader from './studyComponents/StudyHeader';
import { Site } from '../../models';
import { EntityType } from 'adc-sirius-module-common';
import {
  Button,
  Highlight,
  IconBannersWarning,
  LoadingSpinner,
  VStack,
} from '@adc/parallax-component-library';
import DynamicTable, { ColumnConfig } from '../table/DynamicTable';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  devicesByStudyQueryOptions,
  sitesQueryOptions,
  studyCapabilitiesQueryOptions,
  studyQueryOptions,
  subjectsQueryOptions,
} from '../../queryOptions';
import { AssignDeviceModal } from '../deviceAssignment/AssignDeviceModal';
import { useErrorMessageOnEntityView } from '../../hooks/useErrorMessageOnEntityView';
import { SitesTable } from './studyComponents/SitesTable';
import { SubjectsTable } from './studyComponents/SubjectsTable';

/**
 * Study component that displays the details of a specific study.
 * @returns {React.JSX.Element}
 */
export default function Study() {
  const navigateWithBreadcrumbs = useNavigateWithBreadcrumbs();

  const navigateToSite = (siteKey: string, siteLabel: string) => {
    navigateWithBreadcrumbs(
      ROUTES.SITES.DETAIL.replace(':siteKey', siteKey),
      siteLabel,
      study.data?.name || i18n.t('Page_Study.title'),
    );
  };

  const navigateToSubject = (subjectKey: string, subjectLabel: string) => {
    navigateWithBreadcrumbs(
      ROUTES.SUBJECTS.DETAIL.replace(':subjectKey', subjectKey),
      subjectLabel,
      study.data?.name || i18n.t('Page_Study.title'),
    );
  };

  const { studyKey } = useParams();

  const { data: capabilities } = useQuery(
    studyCapabilitiesQueryOptions({ key: studyKey }),
  );

  const [study, sites, devices] = useQueries({
    queries: [
      studyQueryOptions({ key: studyKey }),
      sitesQueryOptions(
        { studyKey },
        { enabled: !!studyKey && !!capabilities?.canGetSites },
      ),
      devicesByStudyQueryOptions(
        {
          key: studyKey,
        },
        { enabled: !!studyKey },
      ),
    ],
  });

  const errorMessage = useErrorMessageOnEntityView(
    study.error,
    'Page_Study.load_error',
  );

  const subjects = useQueries({
    queries: sites.isSuccess
      ? sites.data.map((site: Site) =>
          subjectsQueryOptions(
            { siteKey: site.key },
            { enabled: !!capabilities?.canGetSubjects },
          ),
        )
      : [],
    combine: (results) => {
      return {
        data: results
          .flatMap((result) => result.data)
          .filter((subject) => !!subject),
        pending: results.some((result) => result.isPending),
      };
    },
  });

  const devicesTableColumnData: ColumnConfig[] = [
    { label: i18n.t('Device Name'), dataKey: 'appName' },
    { label: i18n.t('Entity.id'), dataKey: 'deviceId' },
    { label: i18n.t('Device.os'), dataKey: 'os' },
    { label: i18n.t('Device.model'), dataKey: 'phoneModel' },
  ];

  return (
    <VStack gap="$4" height="100%">
      {study.isPending ? (
        <VStack alignItems="center" justifyContent="center" flexGrow={1}>
          <LoadingSpinner testID="study-loading" />
        </VStack>
      ) : null}

      {study.isError ? (
        <Highlight
          variant="danger"
          iconStart={
            <IconBannersWarning
              color="$neutral.0"
              accentColor="$support.danger.default"
            />
          }
          title={i18n.t('Page_Study.load_error_title')}
          body={i18n.t(errorMessage)}
          testID="study-error-banner"
        />
      ) : null}

      {study.isSuccess ? (
        <VStack gap="$4">
          <StudyHeader
            study={study.data}
            isReadonly={!capabilities?.canUpdateStudy}
          />
          {capabilities?.canGetSites ? (
            <SitesTable
              isLoading={sites.isPending}
              hasLoadingError={sites.isError}
              sites={sites.data || []}
              studyKey={study.data.key}
              navigateToSite={navigateToSite}
              canCreateSite={capabilities.canInsertSite}
            />
          ) : null}

          {capabilities?.canGetSubjects ? (
            <SubjectsTable
              isLoading={subjects.pending}
              hasLoadingError={false}
              subjects={subjects.data || []}
              sites={sites.data || []}
              navigateToSite={navigateToSite}
              navigateToSubject={navigateToSubject}
              canCreateSubject={capabilities.canInsertSubject}
            />
          ) : null}
          <DynamicTable
            isLoading={devices.isPending}
            hasLoadingError={devices.isError}
            title={i18n.t('Page_Study.table_devices_title')}
            description={i18n.t('Page_Study.table_devices_description')}
            columnData={devicesTableColumnData}
            rowData={devices.data || []}
            toolbarRightContent={
              <AssignDeviceModal
                entityType={EntityType.Study}
                entityKey={study.data.key}
                entityName={study.data.name}
                trigger={
                  <Button
                    size="medium"
                    label={i18n.t('Page_Study.manage_assignments')}
                    onPress={() => {}}
                    disabled={devices.isPending}
                  />
                }
              />
            }
          />
        </VStack>
      ) : null}
    </VStack>
  );
}
